/*
  Helpers for the Data Dashboard tab

  These are pure functions with no Vue/Vuex dependency so they can be unit
  tested directly. Everything here operates on the `variableData` already held
  in the `datasetSummary` store, which is shaped as:

    variableData.observation[scaleId] = [{subject_id, visit_num, visit_event, value}, ...]
    variableData.subject[scaleId]     = [{subject_id, value}, ...]
*/
import { mean, deviation, min, max, quantile } from 'd3-array';
import {
  sortVisitEvents,
  sortScales,
  scaleSortFn,
  strCompare,
} from './helpers';
import { colors } from './colors';

// Domain used to mark demographic *observation* variables as predictors.
// One of the controlled domains listed in views/Domains.vue.
export const DEMOGRAPHICS_CATEGORY = 'Demographics';

export const DATA_CATEGORY_CONTINUOUS = 'Continuous';
export const DATA_CATEGORY_CATEGORICAL = 'Categorical';

// Levels shown when splitting by a categorical predictor. Beyond this a box
// plot stops being readable; the excess is disclosed, never silently dropped.
export const MAX_SPLIT_LEVELS = 8;

// Levels smaller than this are excluded from both the plot and the test.
// f_oneway/chi2 on singleton groups produces noise, not a result.
export const MIN_GROUP_SIZE = 10;

// Segments drawn in a stacked bar before the tail is hidden and disclosed.
export const MAX_OUTCOME_LEVELS = 8;

// The mean on a box plot is a black dot, the convention the ECharts box plots in
// AnalysisSummary/BoxPlots.vue set. Their symbolSize of 12 suits a full-size
// plot; the default here is sized for a matrix cell, and callers with room ask
// for the larger one.
export const MEAN_MARKER_COLOR = '#000000';
export const MEAN_MARKER_SIZE = 6;
export const MEAN_MARKER_SIZE_LARGE = 12;

// Caps on the default selection. These exist for layout, not cost -- the data
// is already client-side, but 200 cards is unusable. The toolbar always shows
// "{n} / {total}" so the cap is visible to the user.
export const MAX_DEFAULT_PREDICTORS = 8;
export const MAX_DEFAULT_OUTCOMES = 6;

// Used only to order predictors so the usual suspects surface first. A miss
// here degrades sort order, never correctness -- membership is decided by
// isPredictor(), which uses the curated domain instead of the variable name.
const PREDICTOR_NAME_PRIORITY = [
  /\bage\b|^age/i,
  /\bsex\b|^sex|\bgender\b|^gender/i,
  /\brace\b|^race/i,
  /ethnic/i,
];

/**
 * Stable identity for a variable.
 *
 * `id` alone is not unique across the merged variable list: subject variables
 * are keyed on subject_ontology.id and observations on observation_ontology.id,
 * which are independent sequences and do collide. Anything that tracks a
 * selection must key on this instead.
 */
export function variableKey(v) {
  return `${v.type}-${v.id}`;
}

/**
 * Predictor vs. outcome, using the convention already established by automated
 * analysis mode in components/DatasetManager/SharedVariableTable.vue.
 */
export function isPredictor(v) {
  return v.type === 'subject' || v.category === DEMOGRAPHICS_CATEGORY;
}

/**
 * Whether a variable can appear on the dashboard at all. Date-valued variables
 * are unsupported by the charts (see CohortManager/OutputVariableChart.vue),
 * and anything without a recognized data_category has no card layout.
 */
export function isDashboardEligible(v) {
  if (!v || v.value_type === 'date') return false;
  return (
    v.data_category === DATA_CATEGORY_CONTINUOUS ||
    v.data_category === DATA_CATEGORY_CATEGORICAL
  );
}

/**
 * "Longitudinal" is a property of the dataset, not of the variable, so it has
 * to be supplied by the caller. Subject attributes are never longitudinal.
 * Mirrors the derivation in DatasetSummary/VariableSummaryChart.vue.
 */
export function isLongitudinalVariable(v, datasetIsLongitudinal) {
  return v.type === 'observation' && !!datasetIsLongitudinal;
}

function visitKeyFor(visitVariable) {
  return visitVariable === 'Visit Number' ? 'visit_num' : 'visit_event';
}

/**
 * The first visit configured on a collection's observation variable, if any.
 * FETCH_COLLECTION attaches these; studies have no configured window.
 */
export function configuredFirstVisit(variable, visitVariable) {
  const key =
    visitVariable === 'Visit Number' ? 'first_visit_num' : 'first_visit_event';
  const value = variable[key];
  return value === undefined || value === null ? null : value;
}

function variableRows(variableData, variable) {
  const bucket = variableData ? variableData[variable.type] : null;
  const rows = bucket ? bucket[variable.id] : null;
  return Array.isArray(rows) ? rows : [];
}

/**
 * The earliest visit at which this variable was actually recorded, ordered by
 * the app's existing visit ordering. Visits are ranked from rows that carry a
 * value, so a variable first collected at V6 baselines at V6 rather than at an
 * empty V0. Note sortVisitEvents also filters out the PPMI-specific ST/U##/PW
 * events, so those can never be chosen -- consistent with the rest of the app.
 */
function inferredFirstVisit(rows, visitKey) {
  const labels = [];
  const seen = new Set();
  rows.forEach(r => {
    if (r.value == null) return;
    const label = r[visitKey];
    if (label == null) return;
    const key = String(label);
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  });

  const ordered = sortVisitEvents(labels);
  return ordered.length === 0 ? null : ordered[0];
}

/**
 * Reduce a variable to one value per subject, at baseline.
 *
 * Subject attributes and cross-sectional observations have a single value.
 * Longitudinal observations are read at a single visit: the collection's
 * configured first visit when one is set, and otherwise the earliest visit the
 * variable was recorded at. A subject with nothing at that visit counts as
 * missing even if it has values later, which is what "at baseline" has to mean
 * for a variable that changes over the study -- and matches how the rest of the
 * app compares two fixed timepoints rather than each subject's own.
 *
 * @returns {Map} subject_id -> raw value (uncoerced)
 */
export function baselineValuesBySubject(variableData, variable, options = {}) {
  const { longitudinal = false, visitVariable = 'Visit Event' } = options;
  const rows = variableRows(variableData, variable);
  const values = new Map();
  if (rows.length === 0) return values;

  if (!isLongitudinalVariable(variable, longitudinal)) {
    rows.forEach(r => {
      if (r.value == null || values.has(r.subject_id)) return;
      values.set(r.subject_id, r.value);
    });
    return values;
  }

  const visitKey = visitKeyFor(visitVariable);
  const configured = configuredFirstVisit(variable, visitVariable);
  const firstVisit =
    configured === null ? inferredFirstVisit(rows, visitKey) : configured;

  // no visit carries a value, so there is nothing to read at baseline
  if (firstVisit === null) return values;

  rows.forEach(r => {
    if (r.value == null || values.has(r.subject_id)) return;
    if (String(r[visitKey]) !== String(firstVisit)) return;
    values.set(r.subject_id, r.value);
  });

  return values;
}

/**
 * Coerce to numbers the way the existing sparklines do (`+d.value`), dropping
 * anything that isn't finite.
 */
export function toNumbers(values) {
  const nums = [];
  values.forEach(v => {
    if (v == null || v === '') return;
    const n = Number(v);
    if (Number.isFinite(n)) nums.push(n);
  });
  return nums;
}

/**
 * Distinct values and their subject counts, most frequent first. Ties break
 * alphabetically so the ordering (and therefore the reference group) is stable
 * across runs.
 */
export function levelCounts(values) {
  const counts = new Map();
  values.forEach(v => {
    if (v == null || v === '') return;
    const key = String(v);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([level, n]) => ({ level, n }))
    .sort((a, b) => b.n - a.n || strCompare(a.level, b.level));
}

/**
 * Card footer statistics.
 *
 * Missingness is the share of the population with no baseline value for this
 * variable, computed straight from the data rather than via
 * GET_VARIABLE_COUNTS -- that action only produces real counts for studies,
 * and assumes completeness for collections.
 *
 * @param values array of raw baseline values (one per subject)
 * @param variable the variable, used to pick continuous vs. categorical stats
 * @param numSubjects population size; the missingness denominator
 */
export function summaryStats(values, variable, numSubjects) {
  const continuous = variable.data_category === DATA_CATEGORY_CONTINUOUS;
  const present = continuous
    ? toNumbers(values)
    : values.filter(v => v != null && v !== '');
  const n = present.length;

  const stats = {
    n,
    missingPct:
      numSubjects > 0 ? Math.max(0, (1 - n / numSubjects) * 100) : null,
    mean: null,
    sd: null,
    min: null,
    max: null,
    levels: null,
  };

  if (n === 0) return stats;

  if (continuous) {
    stats.mean = mean(present);
    // d3's deviation is the sample SD and is undefined for n < 2
    const sd = deviation(present);
    stats.sd = sd === undefined ? null : sd;
    stats.min = min(present);
    stats.max = max(present);
  } else {
    stats.levels = levelCounts(present).length;
  }

  return stats;
}

/**
 * Format a statistic for a card footer. Precision scales with magnitude so a
 * lab value and a subject count both read sensibly in the same row.
 */
export function formatStat(x) {
  if (x == null || !Number.isFinite(x)) return '—';
  const a = Math.abs(x);
  if (a >= 100) return x.toFixed(0);
  if (a >= 10) return x.toFixed(1);
  return x.toFixed(2);
}

/**
 * Percentages are rounded to whole numbers, except that a small non-zero share
 * must never render as "0%" -- that would read as "nothing missing".
 */
export function formatPercent(p) {
  if (p == null || !Number.isFinite(p)) return '—';
  if (p > 0 && p < 0.5) return '<1%';
  if (p < 100 && p > 99.5) return '>99%';
  return `${Math.round(p)}%`;
}

/**
 * Label value for the top of a sparkline's y axis: the tallest bar's count
 * floored to its leading digit, so 590 reads as 500 and 1234 as 1000. Being at
 * or below the peak means the caller moves the label down the axis instead of
 * rescaling the plot to meet it. Counts below ten have no rounder neighbour, so
 * they are labelled as they are.
 */
export function axisTick(peak) {
  if (!Number.isFinite(peak) || peak < 10) return peak;
  // digit count rather than log10, which is not exact at the powers of ten
  // this floors to
  const magnitude = Math.pow(10, String(Math.floor(peak)).length - 1);
  return Math.floor(peak / magnitude) * magnitude;
}

/**
 * Mean +/- z * SEM, normal approximation. compute-anova returns only a p-value
 * and F statistic, so the interval is computed here.
 */
export function confidenceInterval(values, z = 1.96) {
  const nums = toNumbers(values);
  if (nums.length < 2) return { lower: null, upper: null };

  const sd = deviation(nums);
  if (sd === undefined) return { lower: null, upper: null };

  const m = mean(nums);
  const half = z * (sd / Math.sqrt(nums.length));
  return { lower: m - half, upper: m + half };
}

/**
 * Whether a variable can be used as the "predictor" dimension. v1 supports
 * categorical predictors only; continuous predictors would need binning.
 *
 * @param levels number of distinct levels present after baseline reduction
 */
export function isSplitEligible(v, levels) {
  return (
    isDashboardEligible(v) &&
    isPredictor(v) &&
    v.data_category === DATA_CATEGORY_CATEGORICAL &&
    levels >= 2
  );
}

/**
 * Turn a categorical predictor's baseline values into analysis groups.
 *
 * Levels below minGroupSize are excluded, then the largest maxLevels are kept.
 * Everything excluded is reported back so the UI can disclose it rather than
 * silently truncating. The reference group is the largest surviving level.
 *
 * @param splitValues Map of subject_id -> level, from baselineValuesBySubject
 * @returns {{levels, subjectsByLevel, referenceLevel, dropped, totalLevels, coverage}}
 */
export function deriveGroups(splitValues, options = {}) {
  const {
    maxLevels = MAX_SPLIT_LEVELS,
    minGroupSize = MIN_GROUP_SIZE,
  } = options;

  const values = Array.from(splitValues.values());
  const allLevels = levelCounts(values);
  const totalSubjects = allLevels.reduce((sum, l) => sum + l.n, 0);

  const kept = allLevels.filter(l => l.n >= minGroupSize).slice(0, maxLevels);
  const keptLevels = new Set(kept.map(l => l.level));
  const dropped = allLevels.filter(l => !keptLevels.has(l.level));

  const subjectsByLevel = new Map(kept.map(l => [l.level, []]));
  splitValues.forEach((value, subjectId) => {
    if (value == null || value === '') return;
    const level = String(value);
    if (subjectsByLevel.has(level)) subjectsByLevel.get(level).push(subjectId);
  });

  const keptSubjects = kept.reduce((sum, l) => sum + l.n, 0);

  return {
    levels: kept,
    subjectsByLevel,
    referenceLevel: kept.length > 0 ? kept[0].level : null,
    dropped,
    totalLevels: allLevels.length,
    coverage: totalSubjects > 0 ? keptSubjects / totalSubjects : 0,
  };
}

/**
 * Split one outcome's baseline values across the derived groups.
 *
 * Values are returned raw and in derived.levels order; callers coerce with
 * toNumbers() when they need numbers.
 *
 * @param valuesBySubject Map of subject_id -> value, from baselineValuesBySubject
 * @returns [{level, values}]
 */
export function groupedValues(derived, valuesBySubject) {
  return derived.levels.map(({ level }) => {
    const subjectIds = derived.subjectsByLevel.get(level) || [];
    const values = [];
    subjectIds.forEach(id => {
      const v = valuesBySubject.get(id);
      if (v !== undefined && v !== null && v !== '') values.push(v);
    });
    return { level, values };
  });
}

/**
 * Canonical level order for a categorical outcome: ranked by frequency over the
 * whole population, capped the same way a single cell is capped.
 *
 * Anything shared across a matrix row has to be derived here rather than from a
 * cell. Each predictor drops a different set of subjects, so ranking within a
 * cell can put one outcome level at a different series index -- and so give it
 * a different colour -- than the cell beside it. Ordering once per outcome is
 * what lets a row's plots, its legend, and its drill-down agree.
 *
 * @returns {levels, hidden} -- hidden counts the levels the cap left out
 */
export function outcomeLevelOrder(valuesBySubject, options = {}) {
  const { maxOutcomeLevels = MAX_OUTCOME_LEVELS } = options;
  const ordered = levelCounts(Array.from(valuesBySubject.values()));
  return {
    levels: ordered.slice(0, maxOutcomeLevels).map(l => l.level),
    hidden: Math.max(ordered.length - maxOutcomeLevels, 0),
  };
}

/**
 * Counts of a categorical outcome's levels within each split group, shaped for
 * a stacked bar: one series per outcome level, one count per split level.
 *
 * Outcome levels are ordered by overall frequency and capped, because a stack
 * with thirty segments is unreadable. The remainder is reported rather than
 * dropped silently.
 */
export function crossTabulate(derived, valuesBySubject, options = {}) {
  const { maxOutcomeLevels = MAX_OUTCOME_LEVELS, levelOrder = null } = options;
  const grouped = groupedValues(derived, valuesBySubject);

  const totals = new Map();
  grouped.forEach(g => {
    g.values.forEach(v => {
      const key = String(v);
      totals.set(key, (totals.get(key) || 0) + 1);
    });
  });

  const ordered = Array.from(totals.entries())
    .map(([level, n]) => ({ level, n }))
    .sort((a, b) => b.n - a.n || strCompare(a.level, b.level));

  // A caller sharing one legend across a row supplies the order, so a level
  // keeps its series index -- and its colour -- in every cell. A level absent
  // from this cell still gets its series, holding zeroes, which is what keeps
  // those indices lined up.
  const kept = levelOrder
    ? levelOrder.map(level => ({ level }))
    : ordered.slice(0, maxOutcomeLevels);
  const shown = new Set(kept.map(k => k.level));

  return {
    splitLevels: derived.levels.map(l => l.level),
    series: kept.map(({ level }) => ({
      level,
      counts: grouped.map(
        g => g.values.filter(v => String(v) === level).length
      ),
    })),
    hiddenOutcomeLevels: ordered.filter(o => !shown.has(o.level)).length,
  };
}

/**
 * Five-number summary for one box, computed here instead of by Plotly.
 *
 * Plotly derives quartiles from the raw `y` array, so a trace carries one point
 * per subject. Across a matrix of plots on a study with tens of thousands of
 * subjects that is far too much data to build and hold. Handing Plotly the
 * summary directly keeps every trace the same small size no matter how large
 * the study is. Whiskers follow the Tukey 1.5*IQR convention Plotly uses.
 *
 * @returns null when no value is usable, so callers can skip the box entirely
 */
export function boxStats(values) {
  const nums = toNumbers(values).sort((a, b) => a - b);
  if (nums.length === 0) return null;

  const q1 = quantile(nums, 0.25);
  const q3 = quantile(nums, 0.75);
  const iqr = q3 - q1;
  const lowerLimit = q1 - 1.5 * iqr;
  const upperLimit = q3 + 1.5 * iqr;

  const outliers = [];
  let lowerfence = null;
  let upperfence = null;
  nums.forEach(v => {
    if (v < lowerLimit || v > upperLimit) {
      outliers.push(v);
      return;
    }
    // nums is sorted, so the first and last survivors are the whisker ends
    if (lowerfence === null) lowerfence = v;
    upperfence = v;
  });

  return {
    n: nums.length,
    q1,
    median: quantile(nums, 0.5),
    q3,
    lowerfence: lowerfence === null ? nums[0] : lowerfence,
    upperfence: upperfence === null ? nums[nums.length - 1] : upperfence,
    mean: mean(nums),
    outliers,
  };
}

/**
 * A y-range spanning every box in a matrix row, so that cells for the same
 * outcome stay comparable across predictors instead of each autoscaling to its
 * own group. Padded so whiskers are not clipped by the frame.
 *
 * Only the whiskers are considered. Matrix cells draw no outlier points, and
 * letting a single extreme value into the range would flatten every box in the
 * row into a line.
 *
 * @param statsList boxStats() results, nulls allowed
 * @returns [lo, hi], or null when the row has nothing to draw
 */
export function sharedRange(statsList, padding = 0.05) {
  let lo = null;
  let hi = null;

  statsList.forEach(s => {
    if (!s) return;
    if (lo === null || s.lowerfence < lo) lo = s.lowerfence;
    if (hi === null || s.upperfence > hi) hi = s.upperfence;
  });

  if (lo === null) return null;
  if (lo === hi) return [lo - 1, hi + 1];

  const pad = (hi - lo) * padding;
  return [lo - pad, hi + pad];
}

/**
 * A categorical outcome laid out for reading rather than for stacking: one row
 * per split level, one column per outcome level, each cell a count and its
 * share of the row.
 *
 * The denominator is every subject in the group holding a value for the
 * outcome, including those in levels crossTabulate capped away. A row
 * therefore falls short of 100% exactly when hiddenOutcomeLevels is non-zero.
 */
export function contingencyTable(derived, valuesBySubject, options = {}) {
  const tab = crossTabulate(derived, valuesBySubject, options);
  const grouped = groupedValues(derived, valuesBySubject);

  const rows = tab.splitLevels.map((level, i) => {
    const counts = tab.series.map(s => s.counts[i]);
    const n = grouped[i] ? grouped[i].values.length : 0;
    return {
      level,
      counts,
      n,
      percents: counts.map(c => (n > 0 ? (c / n) * 100 : null)),
    };
  });

  return {
    outcomeLevels: tab.series.map(s => s.level),
    rows,
    hiddenOutcomeLevels: tab.hiddenOutcomeLevels,
  };
}

/**
 * Plotly traces for a continuous cell, built from boxStats() summaries.
 *
 * Supplying q1/median/q3 tells Plotly the quartiles are already computed, so it
 * skips its own pass over the raw values. It draws no outlier points in that
 * mode; they are added as a separate scatter only when asked for, since they
 * earn their place in a full-size plot but are noise in a matrix cell.
 *
 * @param levels split level labels, parallel to statsList
 * @param statsList boxStats() results, nulls allowed for empty groups
 */
export function boxTraces(levels, statsList, options = {}) {
  const { showOutliers = false, meanSize = MEAN_MARKER_SIZE } = options;
  const line = colors['plotly_palette'][0];
  const fill = colors['bar_graphs'][3];

  const drawn = [];
  levels.forEach((level, i) => {
    if (statsList[i]) drawn.push({ level, stats: statsList[i] });
  });
  if (drawn.length === 0) return [];

  const traces = [
    {
      type: 'box',
      x: drawn.map(d => d.level),
      q1: drawn.map(d => d.stats.q1),
      median: drawn.map(d => d.stats.median),
      q3: drawn.map(d => d.stats.q3),
      lowerfence: drawn.map(d => d.stats.lowerfence),
      upperfence: drawn.map(d => d.stats.upperfence),
      showlegend: false,
      line: { color: line, width: 2 },
      fillcolor: fill,
    },
  ];

  if (showOutliers) {
    const x = [];
    const y = [];
    drawn.forEach(d => {
      d.stats.outliers.forEach(v => {
        x.push(d.level);
        y.push(v);
      });
    });

    if (y.length > 0) {
      traces.push({
        type: 'scatter',
        mode: 'markers',
        name: 'outliers',
        x,
        y,
        marker: { size: 4, opacity: 0.4, color: line },
        showlegend: false,
        hoverinfo: 'y',
      });
    }
  }

  // A dot rather than Plotly's `boxmean` dashed line, matching the scatter
  // series AnalysisSummary/BoxPlots.vue overlays on its boxes. Added last so it
  // draws above the box and above any outlier at the same position.
  traces.push({
    type: 'scatter',
    mode: 'markers',
    name: 'mean',
    x: drawn.map(d => d.level),
    y: drawn.map(d => d.stats.mean),
    marker: { size: meanSize, color: MEAN_MARKER_COLOR },
    showlegend: false,
    hoverinfo: 'y',
  });

  return traces;
}

/**
 * Swatch colour per outcome level, by position in the level order.
 *
 * The only assignment of levels to colours in the dashboard. A legend drawn
 * outside the plot has no way to read the trace colours back, so both it and
 * stackedBarTraces go through here instead of indexing the palette themselves.
 */
export function legendEntries(levels) {
  const palette = colors['bar_graphs'];
  return levels.map((level, i) => ({
    level,
    color: palette[i % palette.length],
  }));
}

/**
 * Plotly traces for a categorical cell: one stacked series per outcome level.
 */
export function stackedBarTraces(tab, options = {}) {
  const { showLegend = false } = options;
  const swatches = legendEntries(tab.series.map(s => s.level));

  return tab.series.map((s, i) => ({
    x: tab.splitLevels,
    y: s.counts,
    name: s.level,
    type: 'bar',
    showlegend: showLegend,
    marker: {
      color: swatches[i].color,
      line: { color: 'white', width: 1 },
    },
  }));
}

/**
 * The per-subject value shape expected by compute_anova_single_var.
 * The endpoint reads a different key for each of these three cases.
 *
 * Subject variables are never outcomes under isPredictor(), so the bare-value
 * branch is here for completeness rather than for the dashboard's own use.
 */
function outcomeRecord(variable, value, longitudinal) {
  const v = value === undefined ? null : value;
  if (variable.type === 'subject') return v;
  return isLongitudinalVariable(variable, longitudinal)
    ? { firstVisit: v }
    : { value: v };
}

/**
 * The subset of variable metadata that get_multiway_comparison_metadata reads.
 * is_longitudinal isn't on the variable records, so it's synthesized here.
 */
export function anovaOutputVariable(variable, longitudinal) {
  return {
    id: variable.id,
    type: variable.type,
    data_category: variable.data_category,
    is_longitudinal: isLongitudinalVariable(variable, longitudinal),
    label: variable.label,
    abbreviation: variable.abbreviation,
    category: variable.category,
  };
}

/**
 * Build the POST body for /api/compute-anova from derived groups.
 *
 * cohort_ids and collection_id are deliberately omitted: supplying them makes
 * the endpoint persist an Analysis row and auto-create a channel, which the
 * dashboard must not do.
 *
 * @param derived result of deriveGroups()
 * @param outcomes [{variable, values}] where values is a Map from
 *        baselineValuesBySubject()
 */
export function buildAnovaPayload(derived, outcomes, options = {}) {
  const { longitudinal = false } = options;

  const groups = derived.levels.map(({ level }) => {
    const subjectIds = derived.subjectsByLevel.get(level) || [];
    return subjectIds.map(subjectId => {
      const record = { subject_id: subjectId };
      outcomes.forEach(({ variable, values }) => {
        record[String(variable.id)] = outcomeRecord(
          variable,
          values.get(subjectId),
          longitudinal
        );
      });
      return record;
    });
  });

  return {
    numGroups: groups.length,
    groups,
    outputVariables: outcomes.map(o =>
      anovaOutputVariable(o.variable, longitudinal)
    ),
    comparisonField: 'firstVisit',
  };
}

/**
 * Build the POST body for /api/compute-pairwise-tukeyhsd for ONE outcome.
 *
 * Three things differ from the ANOVA payload, all forced by the endpoint:
 *
 *  - groups are {id, label, data} rather than bare arrays;
 *  - subjects with no value are dropped here. compute_pairwise_tukeyhsd appends
 *    to its group vector unconditionally but to its data vector only when the
 *    value is non-null, so any null desynchronizes the two and statsmodels
 *    raises;
 *  - values are coerced to numbers. compute-anova calls float() itself, but
 *    this endpoint feeds them straight to pairwise_tukeyhsd.
 *
 * comparisonField must be set correctly per call: unlike compute-anova, this
 * endpoint does not override it for cross-sectional variables.
 */
export function buildTukeyPayload(
  derived,
  outcome,
  valuesBySubject,
  options = {}
) {
  const { longitudinal = false } = options;
  const field = isLongitudinalVariable(outcome, longitudinal)
    ? 'firstVisit'
    : 'value';
  const key = String(outcome.id);

  const groups = derived.levels.map(({ level }) => {
    const subjectIds = derived.subjectsByLevel.get(level) || [];
    const data = [];
    subjectIds.forEach(subjectId => {
      const raw = valuesBySubject.get(subjectId);
      if (raw === undefined || raw === null || raw === '') return;
      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      data.push({ subject_id: subjectId, [key]: { [field]: n } });
    });
    return { id: level, label: level, data };
  });

  return {
    groups,
    outputVariables: [anovaOutputVariable(outcome, longitudinal)],
    comparisonField: field,
  };
}

/**
 * Map each non-reference level to its p-value against the reference.
 *
 * statsmodels returns one flat p-value list over combinations(unique_groups, 2)
 * in the order the endpoint reports them; this walks the same nested index as
 * AnalysisSummary/TukeyHSDHeatmap.vue.
 */
export function pairwisePValuesVsReference(
  uniqueGroups,
  pvals,
  referenceLevel
) {
  const out = new Map();
  if (!Array.isArray(uniqueGroups) || !Array.isArray(pvals)) return out;

  const ref = String(referenceLevel);
  let ind = 0;
  for (let i = 0; i < uniqueGroups.length; i += 1) {
    for (let j = i + 1; j < uniqueGroups.length; j += 1) {
      const a = String(uniqueGroups[i]);
      const b = String(uniqueGroups[j]);
      const p = pvals[ind] == null ? null : pvals[ind];
      if (a === ref) out.set(b, p);
      else if (b === ref) out.set(a, p);
      ind += 1;
    }
  }
  return out;
}

/**
 * Per-group descriptive statistics for a continuous outcome. The confidence
 * interval is computed here because compute-anova returns only p and F.
 */
export function groupStats(derived, valuesBySubject) {
  return groupedValues(derived, valuesBySubject).map(g => {
    const nums = toNumbers(g.values);
    const ci = confidenceInterval(nums);
    return {
      level: g.level,
      n: nums.length,
      mean: nums.length > 0 ? mean(nums) : null,
      ciLower: ci.lower,
      ciUpper: ci.upper,
    };
  });
}

/**
 * Conventional p-value rendering; below the reporting floor it becomes an
 * inequality rather than a string of zeroes.
 */
export function formatPValue(p) {
  if (p == null || !Number.isFinite(p)) return '—';
  if (p < 0.001) return 'p<0.001';
  return `p=${p.toFixed(3)}`;
}

/**
 * Sort rank used to float age/sex/race/ethnicity to the top of the predictor
 * list. Unmatched predictors sort after all matched ones.
 */
export function predictorSortKey(v) {
  const names = [v.abbreviation, v.label, v.scale].filter(Boolean);
  for (let i = 0; i < PREDICTOR_NAME_PRIORITY.length; i += 1) {
    if (names.some(n => PREDICTOR_NAME_PRIORITY[i].test(n))) return i;
  }
  return PREDICTOR_NAME_PRIORITY.length;
}

/**
 * Drop uids the dataset no longer offers.
 *
 * A stored selection goes stale whenever a collection's variables are edited or
 * a study is re-uploaded, so restoring one has to be filtered against what is
 * actually available now.
 */
export function retainAvailable(ids, available) {
  if (!Array.isArray(ids)) return [];
  const offered = new Set((available || []).map(v => v.uid));
  return ids.filter(id => offered.has(id));
}

/**
 * A stored selection reconciled against the current variables, or null when
 * nothing survived.
 *
 * Null rather than an empty array, because a restore that matched nothing
 * should fall back to the defaults. Leaving it empty would strand the user on a
 * blank dashboard whose only way out is the reset button.
 */
export function reconcileSelection(storedIds, available) {
  if (!Array.isArray(storedIds)) return null;
  const kept = retainAvailable(storedIds, available);
  return kept.length > 0 ? kept : null;
}

/**
 * Initial toolbar selection. Studies vary and nothing can be assumed present,
 * so this leans on the domain convention rather than on specific variable
 * names.
 */
export function selectDefaultVariables(variables, options = {}) {
  const {
    maxPredictors = MAX_DEFAULT_PREDICTORS,
    maxOutcomes = MAX_DEFAULT_OUTCOMES,
  } = options;

  const eligible = (variables || []).filter(isDashboardEligible);

  const predictors = eligible
    .filter(isPredictor)
    .sort(
      (a, b) => predictorSortKey(a) - predictorSortKey(b) || scaleSortFn(a, b)
    )
    .slice(0, maxPredictors);

  // sortScales already puts subject variables first, then domain, then label
  const outcomes = sortScales(eligible.filter(v => !isPredictor(v))).slice(
    0,
    maxOutcomes
  );

  return { predictors, outcomes };
}
